/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Mon Apr 22 2024
 **/

import { useMemo } from "react";

import { AvatarStatus, Row, Typography, TypographyTooltip } from "@/frameworks/designStuff/components";
import { Maybe } from "@/frameworks/libraries/components";
import { Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";

import { usePhoneCall } from "@/frameworks/core/hooks";
import { usePersistActions, usePersistStore } from "@/frameworks/core/persistence";
import { AvatarStatusColorMap } from "@/frameworks/core/components";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LOGTAG = "#Profile#";

export default function Profile(props: PushServices.Call.PhoneDetailProps & { isBlockedByMe?: boolean }) {
	const phoneCall = usePhoneCall();
	const dispatch = usePersistActions();
	const callees = phoneCall.state.callees[0];
	const callUsers = phoneCall.state.users(props.callID ?? "")[0];
	const activeStatus = usePersistStore(
		(state) => state.users[callees.length > 0 ? callees[0].userID : ""]?.activeStatus
	);
	const detailInfo = phoneCall.state.phone_detail[0];
	const participatedUserIDList = phoneCall.state.participatedUserList(props.callID || "");
	const callStatus = phoneCall.state.call(props.callID)?.[0].status;

	const callUserList = (
		callStatus === "ONGOING"
			? Object.values(callUsers).filter((user) => user.status === "ACCEPTED" || user.status === "REQUESTED")
			: callees
	).filter((user) => user.userId !== Configure.userID);
	const nameList = useMemo(
		() => callees.filter((callee) => participatedUserIDList.includes(callee?.userID)).map((callee) => callee.name),
		[callees, participatedUserIDList]
	);
	const names = nameList.length === 0 ? [props.callees[0]?.name] : nameList;
	const formatNames = (nameList?: (string | undefined)[]) => {
		if (!nameList) return "";
		if (nameList.length === 0) return "";
		if (nameList.length === 1) return nameList[0];

		const allExceptLast = nameList.slice(0, -1);
		const lastElement = nameList[nameList.length - 1];

		return `${allExceptLast.join(",")},${lastElement}`;
	};
	const isGroupCallHistory = detailInfo.tabID === "group-call-history";
	const caller = useMemo(() => props.callerID && dispatch.users.get_user(props.callerID), [props.callerID]);
	return (
		callees.length > 0 && (
			<Maybe
				test={isGroupCallHistory}
				fallback={
					<Row verticalAlign="center" horizonAlign="center" style={{ width: "100%" }}>
						{names && (
							<Row css={{ width: "100%" }} horizonAlign="center" verticalAlign="center" gap={8}>
								{names.length === 1 && (
									<Row>
										<AvatarStatus
											status={
												callees[0]
													? AvatarStatusColorMap[activeStatus ?? "OFFLINE"] ?? "Gray"
													: "Red"
											}
											size={"Medium"}
										/>
									</Row>
								)}
								<Row css={{ maxWidth: "calc(100% - 18px)" }}>
									<TypographyTooltip.p
										tooltipProps={{
											hasIcon: false
										}}
										componentProps={{
											typoSize: "Head6",
											ellipsis: true,
											maxLine: 1,
											stretch: true,
											align: "center"
										}}
									>
										{formatNames(names || [])}
										{props.isBlockedByMe && " (차단됨)"}
									</TypographyTooltip.p>
								</Row>
							</Row>
						)}
					</Row>
				}
			>
				<Maybe
					test={callees.length <= 4}
					fallback={
						<TypographyTooltip.p
							tooltipProps={{
								hasIcon: false
							}}
							componentProps={{
								typoSize: "Head6",
								ellipsis: true,
								maxLine: 1,
								stretch: true,
								align: "center"
							}}
						>
							{_T("{{name}}의 그룹통화", {
								name: (caller && caller?.name) ?? ""
							})}
						</TypographyTooltip.p>
					}
				>
					<Typography.div
						typoSize="Head6"
						ellipsis
						style={{
							overflow: "hidden",
							textAlign: "center"
						}}
					>
						<Maybe test={callUserList.length > 1} fallback={callUserList[0]?.name}>
							{_T("{{name}} 외 {{count}}명", {
								name: callUserList[0]?.name,
								count: callUserList.length - 1
							})}
						</Maybe>
					</Typography.div>
				</Maybe>
			</Maybe>
		)
	);
}
